import { CACHE_SCHEMA_VERSION, IDLE_WIPE_MS } from './policy';

/**
 * The offline store: one IndexedDB database, everything sensitive inside it.
 *
 * Raw IndexedDB rather than a wrapper library. This file is the whole of the
 * app's PHI-at-rest surface, and it is worth being able to read every write
 * and every delete in one screen without a dependency's semantics in between.
 *
 * ── Why not localStorage ───────────────────────────────────────────
 *
 * docs/14 is categorical: no PHI in localStorage. Beyond the policy, it is
 * synchronous, string-only, and has no expiry — three properties that make an
 * enforced 30-day life impossible to implement honestly.
 *
 * ── The shared-handset rule ────────────────────────────────────────
 *
 * Factory phones get passed around. The store records which employee it
 * belongs to, and `openFor()` destroys the entire database before populating
 * whenever that changes. Not "deletes their rows" — destroys it, so a bug in a
 * key prefix cannot leave one worker's certificate readable in another's
 * session.
 *
 * ── What empties it ────────────────────────────────────────────────
 *
 *   · logout, immediately and locally, without waiting on the network
 *   · a different employee signing in on the same handset
 *   · 14 days without the app being opened
 *   · a schema version bump
 *   · each entry's own expiry, swept on every open
 */

const DB_NAME = 'dru-offline';
const STORE = 'cache';
const META = 'meta';

/** The single row in `meta`, describing whose store this is. */
interface Meta {
  id: 'meta';
  employeeId: string;
  schemaVersion: number;
  lastOpenedAt: number;
}

interface Entry {
  /** `${employeeId}:${key}` — the employee id is in the key as well as the
   *  meta row, so a mis-scoped read cannot silently succeed. */
  id: string;
  employeeId: string;
  key: string;
  value: unknown;
  storedAt: number;
  expiresAt: number;
}

function promisify<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function openRaw(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'id' });
      }
      if (!db.objectStoreNames.contains(META)) {
        db.createObjectStore(META, { keyPath: 'id' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
    // A private window, or a browser set to block site data. Not an error the
    // worker can act on: the app must work online without a store.
    req.onblocked = () => reject(new Error('The offline store is blocked.'));
  });
}

export function destroy(): Promise<void> {
  return new Promise((resolve) => {
    const req = indexedDB.deleteDatabase(DB_NAME);
    // Resolve on every outcome. A failed local wipe must never block sign-out
    // — a worker handing over their phone cannot be told "try again online".
    req.onsuccess = () => resolve();
    req.onerror = () => resolve();
    req.onblocked = () => resolve();
  });
}

/**
 * The offline store for one employee.
 *
 * Every method is failure-tolerant by design: the store is a convenience, and
 * an app that breaks because IndexedDB is unavailable — a private window, a
 * browser blocking site data, a full quota — is worse than one with no offline
 * support at all.
 */
export class OfflineStore {
  private constructor(
    private readonly db: IDBDatabase,
    readonly employeeId: string,
  ) {}

  /**
   * Opens the store for this employee, wiping it first if it belonged to
   * someone else, is from an older schema, or has sat unopened past the idle
   * limit.
   *
   * Returns null when IndexedDB is unusable. Callers treat that as "no cache",
   * never as an error.
   */
  static async openFor(employeeId: string): Promise<OfflineStore | null> {
    try {
      let db = await openRaw();
      const meta = await promisify<Meta | undefined>(
        db.transaction(META, 'readonly').objectStore(META).get('meta'),
      );

      const stale =
        meta !== undefined &&
        (meta.employeeId !== employeeId ||
          meta.schemaVersion !== CACHE_SCHEMA_VERSION ||
          Date.now() - meta.lastOpenedAt > IDLE_WIPE_MS);

      if (stale) {
        // Destroy rather than clear. If a key prefix is ever wrong, clearing
        // rows leaves the mistake readable; deleting the database does not.
        db.close();
        await destroy();
        db = await openRaw();
      }

      const store = new OfflineStore(db, employeeId);
      await store.writeMeta();
      await store.sweep();
      return store;
    } catch {
      return null;
    }
  }

  private async writeMeta(): Promise<void> {
    const meta: Meta = {
      id: 'meta',
      employeeId: this.employeeId,
      schemaVersion: CACHE_SCHEMA_VERSION,
      lastOpenedAt: Date.now(),
    };
    await this.tx(META, 'readwrite', (s) => s.put(meta)).catch(() => undefined);
  }

  /** Drops everything past its expiry. Runs on open, so it always runs. */
  async sweep(now = Date.now()): Promise<void> {
    try {
      const all = await this.tx<Entry[]>(STORE, 'readonly', (s) => s.getAll());
      const dead = all.filter((e) => e.expiresAt <= now || e.employeeId !== this.employeeId);
      if (!dead.length) return;
      await this.tx(STORE, 'readwrite', (s) => {
        for (const e of dead) s.delete(e.id);
        return s.count();
      });
    } catch {
      // A sweep that cannot run leaves stale rows, and `read` checks expiry
      // again before returning anything. Nothing expired is ever served.
    }
  }

  async read<T>(key: string): Promise<{ value: T; storedAt: number } | null> {
    try {
      const row = await this.tx<Entry | undefined>(
        STORE, 'readonly', (s) => s.get(this.id(key)));
      if (!row) return null;

      // Checked here as well as in the sweep. Serving one expired record is
      // worse than never having cached it, and this is the last gate.
      if (row.expiresAt <= Date.now() || row.employeeId !== this.employeeId) {
        await this.remove(key);
        return null;
      }
      return { value: row.value as T, storedAt: row.storedAt };
    } catch {
      return null;
    }
  }

  async write(key: string, value: unknown, expiresAt: number): Promise<void> {
    const entry: Entry = {
      id: this.id(key),
      employeeId: this.employeeId,
      key,
      value,
      storedAt: Date.now(),
      expiresAt,
    };
    try {
      await this.tx(STORE, 'readwrite', (s) => s.put(entry));
    } catch {
      // Quota exhausted, or the store went away mid-session. The screen has
      // the fresh data it just fetched; only the offline copy is lost.
    }
  }

  async remove(key: string): Promise<void> {
    await this.tx(STORE, 'readwrite', (s) => s.delete(this.id(key))).catch(() => undefined);
  }

  close(): void {
    try { this.db.close(); } catch { /* already closed */ }
  }

  private id(key: string): string {
    return `${this.employeeId}:${key}`;
  }

  private tx<T>(
    name: string,
    mode: IDBTransactionMode,
    run: (store: IDBObjectStore) => IDBRequest<any>,
  ): Promise<T> {
    return new Promise((resolve, reject) => {
      const t = this.db.transaction(name, mode);
      const req = run(t.objectStore(name));
      req.onsuccess = () => resolve(req.result as T);
      req.onerror = () => reject(req.error);
      t.onabort = () => reject(t.error);
    });
  }
}
